/**
 * §7.24 Pandemic Potential Index (PPI) — Phase 8.1 of the decision-grade plan.
 *
 * "Is this the next COVID?" — a forward-looking screening signal built to be
 * INTERROGATED, not obeyed. A 0-100 composite of six weighted factors
 * (Novelty, Transmissibility, Velocity, Severity, Genomic divergence,
 * Containment difficulty) with three explicit honesty rails:
 *
 *   1. Visible uncertainty band (±N) on the score — never a point estimate
 *   2. Hoverable factor breakdown so the operator can see WHY the score is what
 *      it is and which factor moves it most
 *   3. Historical peer anchor — this signal vs. COVID-19 / SARS / H1N1 at
 *      equivalent epidemic-day, so the score has a real-world referent
 *
 * Ported verbatim (functionally) from CD R9 design at
 * `designs/biomax-epi/r9/r9.jsx`. Sample data is illustrative — the live
 * scoring pipeline (cross-modal anomaly detection: wastewater + syndromic +
 * genomic + travel) is the Tier-S build behind this surface.
 *
 * Honesty rail: this is a SCREENING SIGNAL, never a prophecy. Pair every
 * surface that consumes the score with the same "what would raise / lower
 * this" interrogation prompt so the operator probes the signal rather than
 * obeying it.
 */

import { useMemo, useState } from 'react';
import { auditCapture } from '../audit/capture';
import { PinToBriefButton } from '../pack/PinToBriefButton';

interface PpiFactor {
  key: string;
  label: string;
  intensity: number;   // 0-1, the factor's signal strength right now
  weight: number;      // 0-1, contribution weight to the composite
  note: string;        // operator-facing rationale ("R0 est 2.1-3.4, rising")
}

const PPI_FACTORS: readonly PpiFactor[] = [
  { key: 'novelty',     label: 'Novelty',                 intensity: 0.90, weight: 0.20, note: 'new clade, low pop. immunity' },
  { key: 'transmit',    label: 'Transmissibility',        intensity: 0.70, weight: 0.24, note: 'R0 est 2.1–3.4, rising' },
  { key: 'velocity',    label: 'Spread velocity',         intensity: 0.80, weight: 0.20, note: '+4 jurisdictions / 2wk' },
  { key: 'severity',    label: 'Severity (IFR)',          intensity: 0.45, weight: 0.16, note: 'CFR 1.8% (early, biased)' },
  { key: 'genomic',     label: 'Genomic divergence',      intensity: 0.60, weight: 0.12, note: '12 mutations vs reference' },
  { key: 'containment', label: 'Containment difficulty',  intensity: 0.75, weight: 0.08, note: 'conflict-zone, weak surveillance' },
];

interface PpiPeer {
  name: string;
  day: string;
  value: number;
  self?: boolean;
}

export function PandemicPotentialIndex() {
  // Composite = sum(intensity × weight) × 100, rounded.
  const score = useMemo(
    () => Math.round(PPI_FACTORS.reduce((s, f) => s + f.intensity * f.weight, 0) * 100),
    [],
  );

  // Synthetic uncertainty band — the live pipeline computes this from the
  // factor-level CIs; sample uses a fixed ±11.
  const band = 11;

  const tier = score >= 70
    ? { word: 'HIGH POTENTIAL', cssVar: 'var(--bm-threat-1)' }
    : score >= 45
      ? { word: 'ELEVATED',       cssVar: 'var(--bm-threat-2)' }
      : { word: 'WATCH',          cssVar: 'var(--bm-threat-4)' };

  const [hov, setHov] = useState<string | null>(null);

  // Composite contribution bar — width proportional to intensity × weight.
  let acc = 0;
  const segments = PPI_FACTORS.map(f => {
    const width = f.intensity * f.weight * 100;
    const seg = { f, x: acc, width };
    acc += width;
    return seg;
  });
  const total = acc;

  // Historical peers at equivalent epidemic-day — calibration aid, NOT
  // forecast. The "this signal" peer is at the current score; the others are
  // representative anchors illustratively chosen.
  const peers: PpiPeer[] = [
    { name: 'COVID-19',    day: 'day 26', value: 71 },
    { name: 'H1N1 2009',   day: 'day 26', value: 48 },
    { name: 'SARS 2003',   day: 'day 26', value: 55 },
    { name: 'this signal', day: 'day 26', value: score, self: true },
  ];

  // Emit audit on factor hover — the "how did the operator interrogate the
  // score?" is itself a governance signal per the Phase 7.1 design.
  const handleHover = (key: string | null) => {
    if (key === hov) return;
    setHov(key);
    if (key) {
      auditCapture.emit({
        event: { type: 'surface.view', surface: '§7.24', subjectIds: [], payload: { factorHovered: key } },
      });
    }
  };

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.24</span>
        <h1 className="surface__title">Pandemic Potential Index</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Forward-looking 0-100 composite — Novelty × Transmissibility × Velocity ×
          Severity × Genomic × Containment, weighted. Built to be interrogated.
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          padding: '2px 6px', borderRadius: 4,
          color: 'var(--bm-state-stale)',
          background: 'var(--bm-prov-stale-bg)',
          border: '1px solid var(--bm-state-stale)',
        }}>⚠ EDU · ILLUSTRATIVE SAMPLE DATA</span>
        <span style={{ flex: 1 }} />
        <PinToBriefButton
          item={{
            id: 'ppi-current-score',
            surface: '§7.24',
            label: `PPI: ${score} (${tier.word}) ±${band}`,
            value: score,
            context: {
              tier: tier.word,
              uncertaintyBand: band,
              factors: PPI_FACTORS.map(f => ({ key: f.key, intensity: f.intensity, weight: f.weight })),
            },
          }}
        />
      </div>

      <div style={{
        marginTop: 16,
        background: 'var(--bm-bg-panel, var(--bm-bg))',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        padding: 20,
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '200px 1fr', gap: 24,
        }}>
          {/* Score panel */}
          <div style={{
            textAlign: 'center' as const,
            borderRight: '1px solid var(--bm-border)',
            paddingRight: 20,
          }}>
            <div style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 64, fontWeight: 760,
              lineHeight: 1, letterSpacing: '-2px',
              color: tier.cssVar,
            }}>
              {score}
              <span style={{
                fontSize: 20, fontWeight: 500,
                color: 'var(--bm-text-subtle)', marginLeft: 4, letterSpacing: 0,
              }}>±{band}</span>
            </div>
            <div style={{
              display: 'inline-block', marginTop: 10,
              fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700,
              letterSpacing: '.5px',
              color: tier.cssVar,
              border: `1px solid ${tier.cssVar}`, borderRadius: 999,
              padding: '3px 11px',
            }}>{tier.word}</div>
            <div style={{
              marginTop: 10,
              fontFamily: 'var(--bm-font-mono)', fontSize: 10,
              color: 'var(--bm-text-subtle)',
            }}>Pandemic Potential Index · 0–100</div>
          </div>

          {/* Contribution panel */}
          <div>
            <div style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '.6px',
              color: 'var(--bm-text-subtle)', marginBottom: 8,
            }}>Weighted contribution to score · hover a factor</div>

            <div style={{
              display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden',
              border: '1px solid var(--bm-border)', marginBottom: 16,
            }}>
              {segments.map(s => {
                const isHov = hov === s.f.key;
                const intensityShade = 30 + s.f.intensity * 50;
                return (
                  <div
                    key={s.f.key}
                    onMouseEnter={() => handleHover(s.f.key)}
                    onMouseLeave={() => handleHover(null)}
                    style={{
                      width: (s.width / total * 100) + '%',
                      background: isHov
                        ? 'var(--bm-accent, #6ea3ff)'
                        : `color-mix(in srgb, ${tier.cssVar} ${intensityShade}%, var(--bm-bg))`,
                      opacity: hov && !isHov ? 0.4 : 1,
                      transition: 'opacity .12s',
                      cursor: 'pointer',
                    }}
                    title={`${s.f.label}: ${Math.round(s.f.intensity * 100)}% intensity × ${Math.round(s.f.weight * 100)}% weight`}
                  />
                );
              })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {PPI_FACTORS.map(f => {
                const isHov = hov === f.key;
                return (
                  <div
                    key={f.key}
                    onMouseEnter={() => handleHover(f.key)}
                    onMouseLeave={() => handleHover(null)}
                    style={{
                      display: 'grid', gridTemplateColumns: '90px 130px 1fr', gap: 10,
                      alignItems: 'center',
                      padding: '3px 6px', borderRadius: 5,
                      background: isHov ? 'var(--bm-bg-subtle)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      height: 6, background: 'var(--bm-bg)',
                      borderRadius: 3, overflow: 'hidden',
                    }}>
                      <span style={{
                        display: 'block', height: '100%',
                        width: (f.intensity * 100) + '%',
                        background: tier.cssVar,
                        borderRadius: 3,
                      }} />
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--bm-text)' }}>{f.label}</span>
                    <span style={{
                      fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
                      color: 'var(--bm-text-subtle)',
                    }}>{f.note}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Historical peer anchor */}
        <div style={{
          marginTop: 18, borderTop: '1px solid var(--bm-border)', paddingTop: 14,
        }}>
          <div style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '.6px',
            color: 'var(--bm-text-subtle)', marginBottom: 8,
          }}>vs historical outbreaks at equivalent epidemic-day (illustrative)</div>
          {peers.map(p => (
            <div key={p.name} style={{
              display: 'grid', gridTemplateColumns: '100px 1fr 32px', gap: 10,
              alignItems: 'center', marginBottom: 5,
            }}>
              <span style={{
                fontSize: 12,
                color: p.self ? 'var(--bm-text)' : 'var(--bm-text-muted)',
                fontWeight: p.self ? 700 : 400,
              }}>{p.name}</span>
              <span style={{
                height: 8, background: 'var(--bm-bg)',
                borderRadius: 4, overflow: 'hidden',
              }}>
                <span style={{
                  display: 'block', height: '100%',
                  width: p.value + '%',
                  background: p.self ? tier.cssVar : 'var(--bm-text-subtle)',
                  borderRadius: 4,
                }} />
              </span>
              <span style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 12, textAlign: 'right' as const,
                color: p.self ? tier.cssVar : 'var(--bm-text-muted)',
              }}>{p.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        marginTop: 14, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-prov-synthetic, var(--bm-state-stale))',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-text)' }}>Forward-looking screening signal — not a prediction.</b>
        {' '}The score is a weighted composite with a visible uncertainty band; the peer anchor is
        a calibration aid, not a forecast. Always paired with "what would raise/lower this"
        so an operator interrogates it, never obeys it. Live scoring pipeline (cross-modal
        anomaly detection: wastewater + syndromic + genomic + travel) is the Tier-S build behind
        this surface.
      </div>
    </div>
  );
}
